import React from 'react'
import { AppContext } from '../../App'
import { PageStatus } from '../../constants/pageStatus'

import * as Request from '../../utilities/request'
import { ClientGroup, WipData } from '../../../../back-end/utilities/apiDefinitions'
import { Button, Col, Container, FormLabel, Row } from 'react-bootstrap'

import CloseIcon from '../../images/icons/close_panel.svg'
import { FormSelect, SelectOption } from '../ui/Form/Select'
import { Search } from 'react-feather'

import './lineItemHistoryPanel.css'
import { Loading } from '../ui/Loading/Loading'
import JobRows from './jobRow'
import { Grouping } from '../../constants/grouping'

interface OtherJobPanelProps {
	isOpen: boolean
	onClose: () => void
	currentInvoiceID: string
	currentInvoiceClientGroupID: string
	currentGrouping: Grouping
	handleOtherJob: (jobIDs: string[]) => void
}

const OtherJobPanel = (props: OtherJobPanelProps) => {
	const context = React.useContext(AppContext)
	const [clientGroupFetchStatus, setClientGroupFetchStatus] = React.useState<PageStatus>('Loading')
	const [wipFetchStatus, setWipFetchStatus] = React.useState<PageStatus>('Loading')
	const [selectedJobs, setSelectedJobs] = React.useState<string[]>([])

	const [clientGroups, setClientGroups] = React.useState<ClientGroup[]>([])
	const [selectedClientGroupID, setSelectedClientGroupID] = React.useState(props.currentInvoiceClientGroupID)
	const [wipData, setWipData] = React.useState<WipData>({ familyGroups: [], grouping: Grouping.fcje.value })

	React.useEffect(() => {
		const getData = async () => {
			const clientGroupRes = await Request.get(`clientGroup`)
			if (clientGroupRes.data.success) {
				setClientGroups(clientGroupRes.data.clientGroups)
				setClientGroupFetchStatus('Ready')
			} else {
				setClientGroupFetchStatus('Error')
			}
		}

		setClientGroupFetchStatus('Loading')
		if (context.appState.authState.isLoggedIn) {
			getData()
		}
	}, [context.appState.authState])

	React.useEffect(() => {
		const getData = async () => {
			const wipRes = await Request.get(`wip/${props.currentGrouping.value}?where=ClientGroupID==${selectedClientGroupID}`)
			if (wipRes.data.success && wipRes.data.wipData) {
				setWipData(wipRes.data.wipData)
				setWipFetchStatus('Ready')
			} else {
				setWipFetchStatus('Error')
			}
		}

		setWipFetchStatus('Loading')
		if (context.appState.authState.isLoggedIn && selectedClientGroupID) {
			getData()
		}
	}, [context.appState.authState, props.currentInvoiceID, selectedClientGroupID, props.currentGrouping])

	const containerClassName = `mr-3 invoice-history-panel ${!props.isOpen ? 'panel-closed' : ''}`

	const allJobs = React.useMemo(
		() =>
			wipData.grouping === 'fcje'
				? wipData.familyGroups.reduce(
						(jobIDs, familyGroup) => [...jobIDs, ...familyGroup.clients.flatMap((client) => client.jobs).map((job) => job.jobID)],
						[] as string[]
				  )
				: wipData.familyGroups.reduce(
						(jobIDs, familyGroup) => [
							...jobIDs,
							...familyGroup.jobTypes.flatMap((jobType) => jobType.clients.flatMap((client) => client.jobs)).map((job) => job.jobID),
						],
						[] as string[]
				  ),
		[wipData]
	)

	if (clientGroupFetchStatus === 'Loading') {
		return (
			<div className={containerClassName}>
				<Loading show />
			</div>
		)
	}

	return (
		<Container className={containerClassName}>
			<Row className="pb-3">
				<Col onClick={() => props.onClose()} className="panel-close-btn" sm="auto">
					<img src={CloseIcon} alt="Close" className="pr-2" />
					<span className="span-bold span-navy span-body-2">Close</span>
				</Col>
			</Row>
			<Row className="pb-3">
				<Col>
					<FormSelect
						name="client"
						label="Client"
						placeholder={
							<div className="d-flex align-items-center">
								<Search size={14} />
								<span className="pl-2">Search or select a client group</span>
							</div>
						}
						onChange={(e) => setSelectedClientGroupID(e ? (e as SelectOption<string>).value : '')}
						options={clientGroups.map((cg) => ({ value: cg.clientGroupID, label: cg.name })).sort((a, b) => (a.label < b.label ? -1 : 1))}
						value={selectedClientGroupID}
					/>
				</Col>
			</Row>
			{selectedClientGroupID && (
				<>
					<Row className="mb-3">
						<Col>
							<FormLabel>Other Jobs</FormLabel>
						</Col>
						<Col />
						<Col xs="auto">
							<Button
								onClick={() => {
									props.handleOtherJob(selectedJobs.length > 0 ? selectedJobs : allJobs)
									props.onClose()
								}}
								style={{ padding: '6px 12px' }}
							>
								{selectedJobs.length > 0 ? 'Add Selected' : 'Add All'}
							</Button>
						</Col>
					</Row>
					<Row className="px-3">
						<Col className="other-job-box-shadow">
							<Row className="invoice-header py-2">
								<Col sm={2}>
									<span className="span-bold span-navy span-body-2">Client</span>
								</Col>
								<Col sm={7}>
									<span className="span-bold span-navy span-body-2">Job</span>
								</Col>
								<Col sm={2}>
									<span className="span-bold span-navy span-body-2">Value</span>
								</Col>
							</Row>
							<JobRows
								loading={wipFetchStatus === 'Loading'}
								outstandingWip={wipData}
								setSelectedJobs={setSelectedJobs}
								selectedJobs={selectedJobs}
								onClose={props.onClose}
							/>
						</Col>
					</Row>
				</>
			)}
		</Container>
	)
}

export default OtherJobPanel
